import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import SafeIcon from '../common/SafeIcon';
import { FiTrendingUp, FiAlertCircle, FiCheckSquare, FiSquare, FiZap, FiTarget, FiHeart, FiActivity } from 'react-icons/fi';
import { progressData, coachNotes, gearList } from '../data/dummyData';

export default function Dashboard() {
  const chartOptions = {
    backgroundColor: 'transparent',
    tooltip: { 
      trigger: 'axis', 
      backgroundColor: '#0A0A0A',
      borderColor: '#1F1F1F',
      textStyle: { color: '#FFF' },
      axisPointer: { type: 'cross', label: { backgroundColor: '#1F1F1F' } } 
    },
    legend: { data: ['Volume (kg)', 'Intensity Score'], textStyle: { color: '#888' }, bottom: 0 },
    grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
    xAxis: [{ type: 'category', boundaryGap: false, data: progressData.dates, axisLine: { lineStyle: { color: '#333' } }, axisLabel: { color: '#888' } }],
    yAxis: [
      { type: 'value', splitLine: { lineStyle: { color: '#1F1F1F', type: 'dashed' } }, axisLabel: { color: '#888' } },
      { type: 'value', splitLine: { show: false }, axisLabel: { color: '#888' } }
    ],
    series: [
      {
        name: 'Volume (kg)', type: 'line', smooth: true,
        itemStyle: { color: '#FFFFFF' },
        lineStyle: { width: 2, color: '#FFFFFF' },
        areaStyle: { color: { type: 'linear', x: 0, y: 0, x2: 0, y2: 1, colorStops: [{ offset: 0, color: 'rgba(255,255,255,0.1)' }, { offset: 1, color: 'rgba(255,255,255,0)' }] } },
        data: progressData.volume
      },
      {
        name: 'Intensity Score', type: 'line', yAxisIndex: 1, smooth: true,
        itemStyle: { color: '#CCFF00' },
        lineStyle: { width: 3, color: '#CCFF00', shadowColor: 'rgba(204,255,0,0.5)', shadowBlur: 10 },
        data: progressData.intensity
      }
    ]
  };

  return (
    <div className="space-y-6">
      {/* Top Row: Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-panel p-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><SafeIcon icon={FiZap} className="text-5xl text-lime-500" /></div>
          <h3 className="text-gray-400 text-[10px] font-mono mb-2 uppercase tracking-widest">CNS_Readiness</h3>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-white tracking-tighter">82</span>
            <span className="text-lime-500 text-lg font-mono mb-1">%</span>
          </div>
          <div className="mt-4 h-1 w-full bg-carbon-700 rounded-full overflow-hidden">
            <motion.div initial={{ width: 0 }} animate={{ width: '82%' }} transition={{ duration: 1, ease: "easeOut" }} className="h-full bg-lime-500 glow-lime" />
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-panel p-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity"><SafeIcon icon={FiHeart} className="text-5xl text-red-500" /></div>
          <h3 className="text-gray-400 text-[10px] font-mono mb-2 uppercase tracking-widest">Avg_BPM</h3>
          <div className="flex items-end gap-2">
            <span className="text-4xl font-bold text-white tracking-tighter">62</span>
            <span className="text-gray-500 text-sm font-mono mb-1">RESTING</span>
          </div>
          <div className="flex gap-1 mt-4 h-1 items-end">
            {[2, 4, 3, 5, 2, 6, 4, 5, 3].map((h, i) => <div key={i} className="flex-1 bg-red-500/50 rounded-t" style={{height: `${h*20}%`}} />)}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-panel p-6 border-lime-500/30 glow-lime">
          <div className="flex justify-between items-start mb-2">
            <h3 className="text-lime-500 text-[10px] font-mono uppercase tracking-widest">Next.Session</h3>
            <span className="bg-lime-500/10 text-lime-500 text-[9px] px-1.5 py-0.5 rounded border border-lime-500/20 font-mono">T-MINUS 04:00</span>
          </div>
          <div className="text-lg font-bold text-white truncate">Hypertrophy Alpha</div>
          <p className="text-[10px] text-gray-500 mt-1 uppercase">Lower Body • 75 Mins</p>
          <button className="mt-4 w-full bg-lime-500 hover:bg-lime-400 text-carbon-900 font-bold py-2 rounded transition-colors uppercase text-[10px] tracking-widest">
            Execute
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="glass-panel p-6 flex flex-col justify-between">
          <h3 className="text-gray-400 text-[10px] font-mono mb-2 uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={FiAlertCircle} /> Coach_Uplink</h3>
          <div className="flex-1 bg-carbon-900/50 rounded p-3 border border-carbon-600 border-l-2 border-l-lime-500 text-xs text-gray-300 italic flex items-center">
            "{coachNotes[1].slice(0, 60)}..."
          </div>
        </motion.div>
      </div>

      {/* Middle Row: Progress Chart & Gear */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="glass-panel p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-gray-400 text-xs font-mono uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={FiTrendingUp} /> Load.Trajectory_7D</h3>
            <div className="flex gap-2">
               <span className="text-[10px] font-mono text-white bg-carbon-700 px-2 py-0.5 rounded">WEEKLY</span>
               <span className="text-[10px] font-mono text-gray-500 px-2 py-0.5 rounded">MONTHLY</span>
            </div>
          </div>
          <div className="h-[320px]">
            <ReactECharts option={chartOptions} style={{ height: '100%', width: '100%' }} />
          </div>
        </motion.div>

        <div className="space-y-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="glass-panel p-6">
            <h3 className="text-gray-400 text-xs font-mono mb-4 uppercase tracking-widest flex items-center gap-2"><SafeIcon icon={FiTarget} /> Gear.Checklist</h3>
            <div className="space-y-3">
              {gearList.map((gear) => (
                <div key={gear.id} className="flex items-center gap-3 p-2 hover:bg-carbon-700/50 rounded transition-colors cursor-pointer group">
                  <SafeIcon icon={gear.packed ? FiCheckSquare : FiSquare} className={`text-lg ${gear.packed ? 'text-lime-500' : 'text-gray-500 group-hover:text-gray-400'}`} />
                  <span className={`text-sm ${gear.packed ? 'text-white line-through opacity-50' : 'text-white'}`}>{gear.item}</span>
                </div>
              ))}
            </div>
            <button className="mt-6 w-full border border-carbon-600 hover:border-lime-500 text-gray-400 hover:text-lime-500 py-2 rounded text-[10px] font-mono uppercase transition-all">Add Parameter</button>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="glass-panel p-6 bg-gradient-to-br from-carbon-800 to-carbon-900 border-l-4 border-l-lime-500">
             <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-lime-500 rounded text-carbon-900"><SafeIcon icon={FiActivity} /></div>
                <h4 className="text-white font-bold text-sm">Adaptive Scaling Active</h4>
             </div>
             <p className="text-xs text-gray-500 leading-relaxed">System has detected high CNS fatigue. Suggested 10% load reduction for next session.</p>
          </motion.div>
        </div>
      </div>
    </div>
  );
}